// BroadcastLiveTile.jsx
//
// Live tile for the STREAMED dashboard (BroadcastDash) ONLY. The classic
// LiveStreamTile is untouched and keeps serving the old device dash.
//
// Two source modes, owned by BroadcastDashboard and passed down:
//   mode="music" -> plays ONLY musicLiveStreams (default experience)
//   mode="park"  -> plays ONLY liveStreams (park streamers)
// Tap advances to the next stream WITHIN the current group. onEnd/onError
// advance too. Absence from RTDB advances only after persisting 20s (the
// proven debounce — a single mid-write snapshot must not remount the
// player).
//
// Audio: own react-youtube player path (NOT the multiview sound store).
// Parent passes muted + volume; applied on ready and on every change.
//
// VIP gate lives in the PARENT: non-VIP gets this tile forced to music mode.

import { useEffect, useMemo, useRef, useState } from "react";
import { useLiveData } from "../../hooks/useLiveData";
import { useMusicStreams } from "../../hooks/useMusicStreams";
import YouTube from "react-youtube";
import LiveMusicPlayer from "../Display/LiveMusicPlayer/LiveMusicPlayer";
import {
  BLOCKED_VIDEO_IDS,
  TOP_STREAMERS,
  PRE_RECORDED_NAMES,
} from "./MultiView/multi-view.utils";

// Park list — same logic as the classic tile / multiview.
function getSortedParkStreams(streams) {
  if (!streams) return [];
  return Object.entries(streams)
    .filter(([, data]) => data?.videoId && !BLOCKED_VIDEO_IDS.has(data.videoId))
    .map(([name, data]) => ({
      key: name,
      streamer: data.name || name,
      videoId: data.videoId,
      isTop: TOP_STREAMERS.has(name),
      isLoop: PRE_RECORDED_NAMES.has(name),
    }))
    .sort((a, b) => {
      if (a.isTop !== b.isTop) return b.isTop - a.isTop;
      if (a.isLoop !== b.isLoop) return a.isLoop - b.isLoop;
      return 0;
    });
}

// ── Music favorites — preferred play order. LOOSE by design: an offline
// favorite is simply absent from the live list, so the next one up plays
// first. Matched by channelId (stable) or name. Non-favorites fall in
// after, keeping their original order.
const MUSIC_FAVORITES = [
  { name: "CastleOfFlow", channelId: "UC31RREkfe0qAU9bIkhT4cVw" },
  { name: "DisneyMagicalSymphony", channelId: "UCcNmT5qGD-Cbf-X0wI_Nu_Q" },
  { name: "TheMagicBaton", channelId: "UChpX-n7CE6E_PB7fl5et-9A" },
  { name: "theenchantedwindow", channelId: "UCuppbIXVg-tvltBqTw9WlCg" },
  { name: "Everdreamcastle", channelId: "UCXl-DH-exDVCCn_XtgnMp9Q" },
  { name: "TheKingdomView_Hi", channelId: "UCA2UCThNRhG5vttOv3gw7dQ" },
  { name: "onceuponatask", channelId: "UCs9r3TgPxMyRHFIznZ-bUrw" },
  { name: "mickeyonmainstreet", channelId: "UCoEoW3UowvJ_VswmtIaI3qQ" },
];
function favoriteRank(entry) {
  const i = MUSIC_FAVORITES.findIndex(
    (f) => f.channelId === entry.channelId || f.name === entry.streamer
  );
  return i < 0 ? MUSIC_FAVORITES.length : i;
}

// Music list — favorites first (in MUSIC_FAVORITES order), rest after.
function getMusicStreamList(streams) {
  if (!streams) return [];
  return Object.entries(streams)
    .filter(([, data]) => data?.videoId && !BLOCKED_VIDEO_IDS.has(data.videoId))
    .map(([key, data]) => ({
      key,
      streamer: data.name || key,
      channelId: data.channelId || "",
      videoId: data.videoId,
      isLoop: false,
    }))
    .sort((a, b) => favoriteRank(a) - favoriteRank(b));
}
// Music-mode source switch (park mode unaffected):
//   true  = licensed CDN audio + cover image (Memories-style)
//   false = original YouTube embed, exactly as before
const PRE_RECORDED = true;
const BroadcastLiveTile = ({ mode, muted, volume, timezone, style }) => {
  const { liveStreams, addEndedStream } = useLiveData();
  const { musicStreams } = useMusicStreams();

  const [currentVideoId, setCurrentVideoId] = useState("");
  const ytRef = useRef(null);
  const lmpRef = useRef(null);

  // Pre-recorded music: pipe the parent's shared audio state through
  // LiveMusicPlayer's imperative API (same wiring the old dash uses).
  useEffect(() => {
    if (mode !== "music" || !PRE_RECORDED) return;
    lmpRef.current?.setVolume?.(Math.min(100, Math.max(0, Number(volume) || 0)));
    lmpRef.current?.setMuted?.(!!muted);
  }, [mode, muted, volume]);

  const parkList = useMemo(
    () => getSortedParkStreams(liveStreams),
    [liveStreams]
  );
  const musicList = useMemo(
    () => getMusicStreamList(musicStreams),
    [musicStreams]
  );
  const activeList = mode === "music" ? musicList : parkList;

  // Ref of the latest active list so advance always sees current data.
  const listRef = useRef(activeList);
  listRef.current = activeList;

  // Advance to the next stream WITHIN the current group (wraps).
  const advanceStream = useRef(() => {});
  advanceStream.current = () => {
    const list = listRef.current;
    if (!list.length) {
      setCurrentVideoId("");
      return;
    }
    const idx = list.findIndex((s) => s.videoId === currentVideoId);
    const nextIdx = idx < 0 ? 0 : (idx + 1) % list.length;
    setCurrentVideoId(list[nextIdx].videoId);
  };

  // Mode switch -> jump to the first stream of the new group.
  useEffect(() => {
    setCurrentVideoId(listRef.current[0]?.videoId || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Initial pick once the active list populates.
  useEffect(() => {
    if (!currentVideoId && activeList.length) {
      setCurrentVideoId(activeList[0].videoId);
    }
  }, [activeList, currentVideoId]);

  // Absence must PERSIST 20s before advancing (mid-write snapshots happen).
  useEffect(() => {
    if (!currentVideoId) return;
    const stillLive = activeList.some((s) => s.videoId === currentVideoId);
    if (stillLive) return;
    const t = setTimeout(() => {
      if (!listRef.current.some((s) => s.videoId === currentVideoId)) {
        advanceStream.current();
      }
    }, 20000);
    return () => clearTimeout(t);
  }, [activeList, currentVideoId]);

  // Apply parent-controlled audio state (own path, volume 0-100).
  useEffect(() => {
    const p = ytRef.current?.internalPlayer;
    if (!p) return;
    try {
      p.setVolume(Math.min(100, Math.max(0, Number(volume) || 0)));
      if (muted) p.mute();
      else p.unMute();
    } catch (e) {
      /* player mid-remount */
    }
  }, [muted, volume, currentVideoId]);

  const handleEnded = (videoId) => {
    if (mode === "park" && videoId) addEndedStream(videoId);
    advanceStream.current();
  };
  const handleError = (videoId) => {
    if (mode === "park" && videoId) addEndedStream(videoId);
    advanceStream.current();
  };


  const current = activeList.find((s) => s.videoId === currentVideoId);
  const hasStream = Boolean(current);
    // ── Pre-recorded music mode: Memories-style licensed audio + cover ──
  if (mode === "music" && PRE_RECORDED) {
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: 20,
          overflow: "hidden",
          background: "#ffffff",
          border: "1px solid rgba(11,31,53,0.12)",
          boxShadow: "0 10px 28px rgba(11,31,53,0.16)",
          padding: 10,
          boxSizing: "border-box",
          ...style,
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            borderRadius: 12,
            overflow: "hidden",
            background: "#0b1220",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: 16,
              fontFamily: "'Lato', sans-serif",
              color: "#fff",
            }}
          >
            <div style={{ fontSize: 17, fontWeight: 800 }}>No Music Streams</div>
            <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.7, marginTop: 2 }}>
              Check Back Soon!
            </div>
          </div>
                    <div className="bdlmp" style={{ position: "absolute", inset: 0 }}>
            <style>{`
              .bdlmp .lmp-caption{ display:none; }
              .bdlmp .lmp-bar{
                position:absolute; left:0; right:0; bottom:0; z-index:3;
                background:linear-gradient(to top, rgba(0,0,0,0.65), transparent);
                padding:8px 10px 7px;
              }
            `}</style>
            <LiveMusicPlayer ref={lmpRef} variant="tile" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: 20,
        overflow: "hidden",
        background: "#ffffff",
        border: "1px solid rgba(11,31,53,0.12)",
        boxShadow: "0 10px 28px rgba(11,31,53,0.16)",
        padding: 10,
        boxSizing: "border-box",
        cursor: hasStream ? "pointer" : "default",
        ...style,
      }}
      onClick={() => {
        // Tap advances within the current group.
        if (hasStream) advanceStream.current();
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: 12,
          overflow: "hidden",
          background: "#0b1220",
        }}
      >
        {hasStream ? (
          <>
            <div
              style={{
                position: "absolute",
                top: "-8%",
                left: "-8%",
                width: "116%",
                height: "116%",
              }}
              // react-youtube renders <div><iframe/></div>; make both fill.
              ref={(el) => {
                if (el) {
                  const inner = el.querySelector("div");
                  if (inner) {
                    inner.style.width = "100%";
                    inner.style.height = "100%";
                  }
                }
              }}
            >
              <YouTube
                key={currentVideoId}
                ref={ytRef}
                videoId={currentVideoId}
                opts={{
                  width: "100%",
                  height: "100%",
                  playerVars: {
                    autoplay: 1,
                    mute: 1, // start muted; the audio effect applies state
                    controls: 0,
                    modestbranding: 1,
                    rel: 0,
                    playsinline: 1,
                    enablejsapi: 1,
                    cc_load_policy: 0,
                  },
                }}
                style={{ width: "100%", height: "100%" }}
                onReady={(e) => {
                  try {
                    e.target.unloadModule("captions");
                    e.target.unloadModule("cc");
                  } catch (err) {}
                  try {
                    e.target.setVolume(
                      Math.min(100, Math.max(0, Number(volume) || 0))
                    );
                    if (muted) e.target.mute();
                    else e.target.unMute();
                  } catch (err) {}
                }}
                onPlay={(e) => {
                  // Captions can load in after ready — kill again (multiview
                  // pattern).
                  try {
                    e.target.unloadModule("captions");
                    e.target.unloadModule("cc");
                  } catch (err) {}
                }}
                onEnd={() => handleEnded(currentVideoId)}
                onError={() => handleError(currentVideoId)}
              />
            </div>

            {/* Tap-capture layer */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                background: "transparent",
              }}
            />

            {/* Badge */}
            <div
              style={{
                position: "absolute",
                top: 8,
                left: 8,
                zIndex: 20,
                fontFamily: "'Lato', sans-serif",
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: 1,
                padding: "3px 8px",
                borderRadius: 5,
                color: "#fff",
                background:
                  mode === "park" && current.isLoop ? "#000" : "#e10600",
                boxShadow: "0 0 4px rgba(0,0,0,0.5)",
              }}
            >
              {mode === "music"
                ? "LIVE MUSIC"
                : current.isLoop
                ? "PRE-RECORDED"
                : "LIVE"}
            </div>

            {/* YouTube disclosure tag — upper right, both stream types */}
            <div
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 20,
                fontFamily: "'Lato', sans-serif",
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: 1,
                padding: "3px 8px",
                borderRadius: 5,
                color: "#fff",
                background: "#e10600",
                boxShadow: "0 0 4px rgba(0,0,0,0.5)",
              }}
            >
              YOUTUBE
            </div>
            {/* Streamer handle */}
            <div
              style={{
                position: "absolute",
                bottom: 8,
                left: 8,
                zIndex: 20,
                fontFamily: "'Lato', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                color: "#fff",
                background: "rgba(0,0,0,0.6)",
                padding: "3px 8px",
                borderRadius: 5,
              }}
            >
              @{current.streamer}
            </div>
          </>
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: 16,
              fontFamily: "'Lato', sans-serif",
              color: "#fff",
            }}
          >
            <div style={{ fontSize: 17, fontWeight: 800 }}>
              {mode === "music" ? "No Music Streams" : "No Live Streams"}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                opacity: 0.7,
                marginTop: 2,
              }}
            >
              Check Back Soon!
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BroadcastLiveTile;
